import React, { useState } from 'react';
import { ConfigProvider, Layout, Menu, Tag, Tooltip } from 'antd';
import {
  EnvironmentOutlined,
  BookOutlined,
  CheckSquareOutlined,
  ShoppingOutlined,
  FileTextOutlined,
  PictureOutlined,
  MoreOutlined,
  CloudOutlined,
  CloudSyncOutlined,
  ExclamationCircleOutlined,
  LoadingOutlined,
  ApiOutlined
} from '@ant-design/icons';
import { AuthProvider, useAuth } from './AuthContext';
import LoginComponent from './LoginComponent';
import MapComponent from './components/MapComponent';
import JournalComponent from './components/JournalComponent';
import TodoListComponent from './components/TodoListComponent';
import PacklistManager from './components/PacklistManager';
import DecisionLogComponent from './components/DecisionLogComponent';
import ImageManager from './components/ImageManager';
import ApiDocumentation from './components/ApiDocumentation';
import TractorWelcome from './components/TractorWelcome';
import theme from './theme';
import './App.css';

const { Header, Content } = Layout;

function App() {
  return (
    <ConfigProvider
      theme={{
        token: theme.antdToken,
      }}
    >
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ConfigProvider>
  );
}

function AppContent() {
  const { user, saveStatus, loadStatus, saveError, loadError } = useAuth();
  const [currentView, setCurrentView] = useState('map');

  // Unified status indicator component that shows load and save state in one place
  const StatusIndicator = () => {
    if (!user) return null;

    // Determine the unified status and configuration
    // Priority: errors > active operations (loading/saving/retrying) > synced
    let statusConfig;

    if (loadStatus === 'error') {
      statusConfig = {
        icon: <ExclamationCircleOutlined />,
        color: 'error',
        text: 'Load Error',
        tooltip: loadError || 'Failed to load data'
      };
    } else if (saveStatus === 'error') {
      statusConfig = {
        icon: <ExclamationCircleOutlined />,
        color: 'error',
        text: 'Save Error',
        tooltip: saveError || 'Failed to save data'
      };
    } else if (loadStatus === 'loading') {
      statusConfig = {
        icon: <LoadingOutlined spin />,
        color: 'processing',
        text: 'Loading...',
        tooltip: 'Loading data from cloud'
      };
    } else if (saveStatus === 'saving') {
      statusConfig = {
        icon: <CloudSyncOutlined spin />,
        color: 'processing',
        text: 'Saving...',
        tooltip: 'Saving data to cloud'
      };
    } else if (saveStatus === 'retrying') {
      statusConfig = {
        icon: <CloudSyncOutlined spin />,
        color: 'warning',
        text: 'Retrying...',
        tooltip: saveError || 'Retrying save to cloud'
      };
    } else {
      // Both idle - show synced
      statusConfig = {
        icon: <CloudOutlined />,
        color: 'success',
        text: 'Synced',
        tooltip: 'All data synced with cloud'
      };
    }

    return (
      <Tooltip title={statusConfig.tooltip}>
        <Tag
          icon={statusConfig.icon}
          color={statusConfig.color}
          style={{
            margin: 0,
            fontSize: '12px',
            cursor: 'help',
            minWidth: '95px', // Reserve fixed width to prevent menu shifting
            textAlign: 'center'
          }}
        >
          {statusConfig.text}
        </Tag>
      </Tooltip>
    );
  };

  const menuItems = [
    {
      key: 'map',
      icon: <EnvironmentOutlined />,
      label: 'Field Map',
    },
    {
      key: 'journal',
      icon: <BookOutlined />,
      label: 'Journal',
    },
    {
      key: 'todo',
      icon: <CheckSquareOutlined />,
      label: 'Todo',
    },
    {
      key: 'packlists',
      icon: <ShoppingOutlined />,
      label: 'Packlists',
    },
    {
      key: 'decisions',
      icon: <FileTextOutlined />,
      label: 'Decisions',
    },
    {
      key: 'images',
      icon: <PictureOutlined />,
      label: 'Images',
    },
    {
      key: 'api',
      icon: <ApiOutlined />,
      label: 'API',
    },
  ];

  return (
    <Layout className="App" style={{ minHeight: '100vh' }}>
      <Header className="App-header" style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        padding: '0 24px',
        backgroundColor: theme.colors.primary,
        height: 'auto',
        lineHeight: 'normal',
        paddingTop: '16px',
        paddingBottom: '16px',
        gap: '16px'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flex: '1 1 auto',
          minWidth: '300px',
          gap: '20px'
        }}>
          <h1 style={{
            margin: 0,
            color: theme.colors.textWhite,
            fontSize: '28px',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            flexShrink: 0
          }}>
            🌾 Ovaielo Agritech
          </h1>
          <StatusIndicator />
          {user && (
            <Menu
              mode="horizontal"
              selectedKeys={[currentView]}
              items={menuItems}
              onClick={(e) => setCurrentView(e.key)}
              overflowedIndicator={<MoreOutlined />}
              triggerSubMenuAction="click"
              style={{
                flex: 1,
                minWidth: 0,
                backgroundColor: 'transparent',
                border: 'none',
                color: theme.colors.textWhite
              }}
              theme="dark"
            />
          )}
        </div>
        <div style={{ flex: '0 1 auto', minWidth: 'fit-content' }}>
          <LoginComponent />
        </div>
      </Header>
      <Content style={{
        backgroundColor: theme.colors.background,
        minHeight: 'calc(100vh - 80px)'
      }}>
        {user ? (
          currentView === 'map' ? <MapComponent /> :
          currentView === 'journal' ? <JournalComponent /> :
          currentView === 'todo' ? <TodoListComponent /> :
          currentView === 'packlists' ? <PacklistManager /> :
          currentView === 'decisions' ? <DecisionLogComponent /> :
          currentView === 'images' ? <ImageManager /> :
          currentView === 'api' ? <ApiDocumentation /> :
          <MapComponent />
        ) : (
          <TractorWelcome />
        )}
      </Content>
    </Layout>
  );
}

export default App;
